import {
  FailedGroup,
  FailedMediaListModal,
  FailedMediaListModalVariant,
  FailedRow,
} from '@components/FailedMediaList/FailedMediaListModal';
import { MediaListLink, MediaListLinkVariant } from '@components/FailedMediaList/MediaListLink';
import { pluralize } from '@lib/utils/units';
import {
  CloseRounded,
  CopyAllRounded,
  ErrorRounded,
  ReportProblemRounded,
} from '@mui/icons-material';
import {
  notificationStore,
  RichEventIconKind,
  RichEventNotification,
  RichEventRow,
} from '@stores/Notifications';
import { trackingStore } from '@stores/Tracking';
import { observer } from 'mobx-react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import css from './RichEventCard.module.scss';

function richRowToFailedRow(
  row: RichEventRow,
  onNavigate: (libraryId: string, mediaId: string) => void,
): FailedRow {
  const view = row.viewInLibrary;
  return {
    key: row.key,
    filename: row.filename,
    reason: row.reason,
    isAudio: row.isAudio ?? false,
    thumbnailUrl: row.thumbnailUrl ?? null,
    viewInLibrary: view
      ? {
          libraryId: view.libraryId,
          mediaId: view.mediaId,
          libraryName: view.libraryName,
          onNavigate: () => onNavigate(view.libraryId, view.mediaId),
        }
      : undefined,
    // Intentionally no `status` — the rich-event card already communicates
    // success/warning/error via its variant colour. A per-row pill on top
    // would just repeat what the card already says (and was wrong for the
    // duplicate case in particular, where the row is reachable in-library
    // and shouldn't read as "failed").
  };
}

function IconForKind({ kind }: { kind: RichEventIconKind }) {
  switch (kind) {
    case 'file-rejected':
      return <ReportProblemRounded />;
    case 'duplicate':
      return <CopyAllRounded />;
    case 'processing-error':
      return <ErrorRounded />;
  }
}

function variantToModalVariant(
  variant: RichEventNotification['variant'],
): FailedMediaListModalVariant {
  if (variant === 'success') return 'success';
  if (variant === 'warning') return 'warning';
  return 'error';
}

function variantToLinkVariant(variant: RichEventNotification['variant']): MediaListLinkVariant {
  if (variant === 'success') return 'success';
  if (variant === 'warning') return 'warning';
  return 'error';
}

/** Render a header / description that may contain the `{filename}`
 * placeholder. The filename gets wrapped in a single-line truncating span
 * so a long name ellipsises instead of forcing the rest of the sentence
 * onto a second line. When the placeholder is absent the text is returned
 * verbatim. */
function renderWithFilename(text: string, filename: string | undefined): React.ReactNode {
  if (!filename || !text.includes('{filename}')) return text;
  const [before, after] = text.split('{filename}');
  return (
    <>
      {before}
      <span className={css.inlineFilename}>{filename}</span>
      {after}
    </>
  );
}

/** Wrap one substring inside a longer text in a click-to-open-modal span.
 * Used by the multi-file rejection card so "N files" inside the description
 * itself becomes a modal trigger (matching the "View N rejected files"
 * rectangle below). When the link text isn't found, falls through to the
 * plain text. */
function renderWithModalLink(
  text: React.ReactNode,
  linkText: string | undefined,
  onClick: () => void,
): React.ReactNode {
  if (!linkText || typeof text !== 'string' || !text.includes(linkText)) return text;
  const [before, after] = text.split(linkText);
  return (
    <>
      {before}
      <button type="button" className={css.descriptionLink} onClick={onClick}>
        {linkText}
      </button>
      {after}
    </>
  );
}

export const RichEventCard = observer(function RichEventCard({
  notification,
  onDismiss,
  formatTime,
}: {
  notification: RichEventNotification;
  onDismiss: () => void;
  formatTime: (timestamp: number) => string;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    variant,
    iconKind,
    header,
    description,
    descriptionFilename,
    descriptionLinkText,
    rows,
    subtitle,
    modalTitle,
    primaryAction,
    viewLinkAction,
  } = notification;
  const firstRow = rows[0];
  const thumbUrl = firstRow?.thumbnailUrl ?? null;
  const hasModalContent = rows.length > 0;
  // When the caller passes a subtitle (currently the duplicate card does),
  // render in a BatchCard-like layout: filename of the first row as the
  // title with a "+ N others" tail, then a `Library | status` strip
  // underneath. Other rich events keep the centered header layout.
  const useBatchStyle = !!subtitle && rows.length > 0;
  const othersCount = useBatchStyle ? Math.max(0, rows.length - 1) : 0;
  // `{filename}` placeholders fall back to the first row's filename when
  // the caller didn't pass a specific one — keeps the simple one-file
  // case from having to duplicate the name.
  const filenameForPlaceholders = descriptionFilename ?? firstRow?.filename;

  const openModal = () => {
    if (hasModalContent) {
      trackingStore.uploadRichEventFilesViewed({ kind: iconKind, rowCount: rows.length });
      setIsModalOpen(true);
    }
  };

  const handleDismiss = () => {
    trackingStore.uploadRichEventDismissed({ kind: iconKind, rowCount: rows.length });
    onDismiss();
  };

  // "View in library" closes the modal and collapses the upload-activity
  // popover so the user lands cleanly on the library listing with the
  // matched media flashing. The card itself stays in the panel — the user
  // dismisses it manually (matches the BatchCard lifetime convention).
  const handleViewInLibraryNavigate = useCallback((libraryId: string, mediaId: string) => {
    trackingStore.viewInLibraryClicked({ source: 'rich-event', libraryId, mediaId });
    setIsModalOpen(false);
    notificationStore.setPopoverOpen(false);
  }, []);

  const groups: FailedGroup[] = hasModalContent
    ? [
        {
          key: notification.id,
          rows: rows.map((r) => richRowToFailedRow(r, handleViewInLibraryNavigate)),
        },
      ]
    : [];

  return (
    <>
      <div className={css.card} data-variant={variant}>
        <div className={css.cardRow}>
          <div className={css.thumb}>
            {thumbUrl ? (
              <img src={thumbUrl} alt="" className={css.thumbImage} />
            ) : (
              <div className={css.thumbIcon}>
                <IconForKind kind={iconKind} />
              </div>
            )}
          </div>

          <div className={css.body}>
            {useBatchStyle && firstRow ? (
              <div className={css.title}>
                <span className={css.filename}>{firstRow.filename}</span>
                {othersCount > 0 && (
                  <span className={css.othersCount}>
                    + {othersCount} {othersCount === 1 ? 'other' : 'others'}
                  </span>
                )}
              </div>
            ) : (
              <div className={css.title}>{renderWithFilename(header, filenameForPlaceholders)}</div>
            )}
            {subtitle && (
              <div className={css.meta}>
                {subtitle.libraryUrl ? (
                  <Link to={subtitle.libraryUrl} className={css.libraryLink}>
                    {subtitle.libraryName}
                  </Link>
                ) : (
                  <span className={css.libraryLink}>{subtitle.libraryName}</span>
                )}
                <span className={css.sep} />
                <span className={css.status} data-variant={subtitle.statusVariant ?? 'warning'}>
                  {subtitle.statusText}
                </span>
              </div>
            )}
            {description && !useBatchStyle && (
              <div className={css.desc}>
                {renderWithModalLink(
                  renderWithFilename(description, filenameForPlaceholders),
                  descriptionLinkText,
                  openModal,
                )}
              </div>
            )}
            <div className={css.time}>{formatTime(notification.timestamp)}</div>
          </div>

          <button
            type="button"
            className={css.dismiss}
            onClick={handleDismiss}
            aria-label="Dismiss notification"
          >
            <CloseRounded />
          </button>
        </div>

        {hasModalContent &&
          (viewLinkAction ? (
            <MediaListLink
              action={viewLinkAction}
              variant={variantToLinkVariant(variant)}
              onClick={openModal}
            />
          ) : (
            <MediaListLink
              action="View"
              subtext={pluralize(rows.length, 'file')}
              variant={variantToLinkVariant(variant)}
              onClick={openModal}
            />
          ))}

        {primaryAction && (
          <MediaListLink
            action={primaryAction.label}
            variant="info"
            onClick={primaryAction.onClick}
          />
        )}
      </div>

      {hasModalContent && (
        <FailedMediaListModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={modalTitle}
          metaText={pluralize(rows.length, 'file')}
          groups={groups}
          variant={variantToModalVariant(variant)}
        />
      )}
    </>
  );
});
